import React from 'react';
import { A_BANK_DESCRIPTION, outlookIndices } from '../../utils/capacityOutlook';
import InfoTooltip from './InfoTooltip';

/**
 * Shared multi-year capacity outlook tables (PolicyPlan location matrices and
 * ChargerPlan circuit tooltips).
 *
 * Circuit data shape (SCE only; LADWP / no data -> null):
 *   {
 *     plan_year: 2025,
 *     original_estimated:  [y1..y5],
 *     new_request:         [y1..y5],
 *     estimated_available: [y1..y5],
 *     a_bank:              "Name 220/66",
 *     a_bank_estimated_available: [y1..y5],
 *   }
 *
 * Substation outlook shape (SCE GNA only; null otherwise):
 *   { plan_year: 2025, estimated_available: [y1..y5] }
 */

const SERIES_LENGTH = 5;
const OUTLOOK_COUNT = 4;

const CIRCUIT_ROWS = [
  { key: 'original_estimated', label: 'Original Est. Cap.' },
  { key: 'new_request', label: 'New Request' },
  { key: 'estimated_available', label: 'Est. Available Cap.' },
];

export const SUBSTATION_ROWS = [
  { key: 'estimated_available', label: 'Est. Available Cap.' },
];

const ABANK_ROWS = [
  { key: 'estimated_available', label: 'Est. Available Cap.' },
];

export function ABankInfoTip({ align = 'left' }) {
  return (
    <InfoTooltip label="About A-Bank capacity" align={align} widthClass="w-80">
      {A_BANK_DESCRIPTION}
    </InfoTooltip>
  );
}

const formatCell = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  return numeric.toFixed(2);
};

const aBankOutlookFromAlc = (alc) => {
  if (!alc || typeof alc !== 'object') return null;
  const series = Array.isArray(alc.a_bank_estimated_available)
    ? alc.a_bank_estimated_available
    : [];
  const name = String(alc.a_bank ?? '').trim();
  if (!name && !series.some((v) => v != null && v !== '')) return null;
  return {
    plan_year: alc.plan_year,
    estimated_available: series,
    name: name || null,
  };
};

export function CapacityMatrix({ title, data, rows, emptyHint, className = 'mb-3 last:mb-0' }) {
  if (!data) {
    return (
      <div className={className}>
        <div className="relative text-xs font-semibold text-gray-700 mb-1">{title}</div>
        <p className="text-xs text-gray-400 italic">{emptyHint}</p>
      </div>
    );
  }

  const planYear = Number(data.plan_year);
  const anchored = outlookIndices(planYear, SERIES_LENGTH, OUTLOOK_COUNT);
  const displayIndices =
    anchored.length > 0
      ? anchored
      : Array.from({ length: Math.min(OUTLOOK_COUNT, SERIES_LENGTH) }, (_, i) => i);
  const years = displayIndices.map((i) =>
    Number.isFinite(planYear) ? planYear + i : null
  );

  return (
    <div className={className}>
      <div className="relative text-xs font-semibold text-gray-700 mb-1">{title}</div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs border border-gray-200 tabular-nums">
          <thead>
            <tr className="bg-gray-50">
              <th className="px-2 py-1 text-left font-medium text-gray-500 border-b border-gray-200">
                MW
              </th>
              {years.map((yr, idx) => (
                <th
                  key={displayIndices[idx]}
                  className="px-2 py-1 text-right font-medium text-gray-500 border-b border-gray-200"
                >
                  {yr ?? '—'}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const series = Array.isArray(data[row.key]) ? data[row.key] : [];
              return (
                <tr key={row.key} className="border-b border-gray-100 last:border-b-0">
                  <td className="px-2 py-1 text-left text-gray-600">{row.label}</td>
                  {displayIndices.map((i) => (
                    <td key={i} className="px-2 py-1 text-right text-gray-900">
                      {formatCell(series[i])}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function CircuitOutlookSection({ title, alc, emptyHint }) {
  const aBank = aBankOutlookFromAlc(alc);
  return (
    <div className="mb-3 last:mb-0">
      <CapacityMatrix
        title={title}
        data={alc}
        rows={CIRCUIT_ROWS}
        emptyHint={emptyHint}
        className="mb-2"
      />
      {alc ? (
        <CapacityMatrix
          title={
            <span className="inline-flex items-center gap-1">
              A Bank
              <ABankInfoTip />
              {aBank?.name ? (
                <span className="text-gray-400 font-normal"> · {aBank.name}</span>
              ) : null}
            </span>
          }
          data={
            aBank
              ? { plan_year: aBank.plan_year, estimated_available: aBank.estimated_available }
              : null
          }
          rows={ABANK_ROWS}
          emptyHint="N/A"
          className=""
        />
      ) : null}
    </div>
  );
}
